import React, {
  useCallback,
  useContext,
  Fragment,
  useState,
  useEffect,
  useMemo,
} from "react";
import {
  Button,
  Icon,
  Pane,
  Select,
  Strong,
  Text,
  TickCircleIcon,
} from "evergreen-ui";
import AccountingContext from "../../contexts/accounting";
import useControlHub from "../../hooks/useControlHub";

import ConfigureBankButton from "../../components/BankAccountConfiguration/ConfigureBankButton";
import BankAccountDetails from "../../components/BankAccountConfiguration/BankAccountDetails";

const AccountingIntegrationsScreen = ({ service, setLoadingState }) => {
  const { api, user } = useControlHub();
  const { accounting, setSystem, system, SYSTEMS_NAME_MAP } =
    useContext(AccountingContext);
  const [billing, setBilling] = useState(null);

  useEffect(() => {
    const getBillingData = async () => {
      try {
        setBilling(await api.getBillingData());
      } catch (error) {}
    };
    getBillingData();
  }, [api, user?.company]);

  const handleConnect = useCallback(() => {
    if (!system) {
      return;
    }
    api
      .connectAccountingSystem({ systemName: system.name })
      .then((data) => {
        window.location.href = data["redirect_url"];
      })
      .catch(() => {});
  }, [api, system]);

  const handleDisconnect = useCallback(() => {
    if (!system) {
      return;
    }
    api
      .disconnectAccountingSystem({ systemName: system.name })
      .then(() => {
        setSystem();
      })
      .catch(() => {});
  }, [api, setSystem, system]);

  const handleSystemSelectChange = useCallback(
    (event) => {
      setSystem(event.target.value);
    },
    [setSystem]
  );

  const isNetsuite = useMemo(() => {
    return Boolean(system?.name === "netsuite");
  }, [system?.name]);

  return (
    <Fragment>
      <Pane>
        {system && system.active && system.integrated ? (
          <Pane alignItems="center" display="flex" marginY={16}>
            <Icon color="success" icon={TickCircleIcon} />
            <Text marginX={8}>
              {"Successfully connected to "}
              <Strong>{SYSTEMS_NAME_MAP[system.name]}</Strong>
            </Text>
            {!isNetsuite && (
              <Button
                height={24}
                marginX={8}
                onClick={handleDisconnect}
              >{`Disconnect from ${SYSTEMS_NAME_MAP[system.name]}`}</Button>
            )}
          </Pane>
        ) : (
          <Pane>
            <Select
              onChange={handleSystemSelectChange}
              value={system ? system.name : ""}
            >
              <option disabled value="">
                Select Accounting System
              </option>
              {Object.entries(accounting.systems).map(([name, data]) => (
                <option
                  disabled={!data.active}
                  key={`system option - ${name}`}
                  value={name}
                >
                  {SYSTEMS_NAME_MAP[name]}
                </option>
              ))}
            </Select>
            <Button
              appearance="primary"
              disabled={!system || !system.active || system.integrated}
              marginX={8}
              onClick={handleConnect}
            >
              Connect
            </Button>
          </Pane>
        )}
      </Pane>
      <Pane>
        {billing && billing.requires_plaid_integration && user.company && (
          <ConfigureBankButton
            billingInfo={billing}
            service={service}
            setLoadingState={setLoadingState}
          />
        )}

        {billing && billing.has_bank_configured && (
          <BankAccountDetails accountDetail={billing.bank_account} />
        )}
      </Pane>
    </Fragment>
  );
};

export default AccountingIntegrationsScreen;
